import { motion } from "framer-motion";
import { useFormContext } from "react-hook-form";

import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { SessionInvitesStatus } from "@classview/shared-types";

import {
  AddSessionFormData,
  SessionVisibility,
} from "../types/add-session-schema";

export function VisibilityElement() {
  const form = useFormContext<AddSessionFormData>();
  const { organisation } = useOrganisation();
  const invitedOrganisations = form.getValues("invitedOrganisations");
  const invitedOrganisationNames: string[] = [];

  if (!invitedOrganisations.find((org) => org.id === organisation.id)) {
    invitedOrganisationNames.push(organisation.name);
  }

  invitedOrganisationNames.push(
    ...invitedOrganisations
      .filter(
        (org) =>
          org.status === SessionInvitesStatus.ACCEPTED ||
          org.status === SessionInvitesStatus.HOST,
      )
      .map((org) => org.name),
  );

  const visibility = form.watch("visibility");

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="visibility"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value}
                className="flex flex-col space-y-1"
              >
                <FormItem className="flex space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value={SessionVisibility.PRIVATE} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal">Private</FormLabel>
                    <FormDescription>
                      Only invited attendees and admins can see this session
                    </FormDescription>
                  </div>
                </FormItem>
                {/* Confidential toggle under Private with animation */}
                <motion.div
                  className="overflow-clip"
                  initial={{ opacity: 0, height: 0 }}
                  animate={
                    visibility === SessionVisibility.PRIVATE
                      ? { opacity: 1, height: "auto" }
                      : { opacity: 0, height: 0 }
                  }
                  transition={{ duration: 0.3 }}
                  style={{ overflowClipMargin: 5 }}
                >
                  <FormField
                    control={form.control}
                    name="isConfidential"
                    render={({ field }) => (
                      <FormItem className="mt-2 flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="font-normal">
                            Confidential session
                          </FormLabel>
                          <FormDescription>
                            Restricts visibility to invited attendees only.
                            Admins will not have access unless they’re
                            specifically invited.
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </motion.div>
                <FormItem className="flex space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem
                      value={SessionVisibility.ORGANISATION_WIDE}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal">
                      Organisation wide
                    </FormLabel>
                    <FormDescription>
                      Everyone within{" "}
                      <span className="italic">
                        {invitedOrganisationNames.join(", ")}
                      </span>{" "}
                      organisation
                      {invitedOrganisationNames.length > 1 ? "s" : ""} will be
                      able to see this session
                    </FormDescription>
                  </div>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
